import axios from 'axios';
import { EmailCredentials, EmailData, EmailResponse, ConnectionTestResponse } from '../types/email';

let API_BASE_URL = process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:5000/api' : 'https://node-mailer-production.up.railway.app/api');

// Fix for missing 'h' in https:// - ensure URL starts with http:// or https://
if (API_BASE_URL && !API_BASE_URL.startsWith('http://') && !API_BASE_URL.startsWith('https://')) {
  if (API_BASE_URL.startsWith('ttps://')) {
    API_BASE_URL = 'h' + API_BASE_URL;
  } else {
    // Fallback to production URL if format is invalid
    API_BASE_URL = 'https://node-mailer-production.up.railway.app/api';
  }
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout for file uploads
});

export const emailApi = {
  // Test email connection (supports both OAuth and password)
  testConnection: async (credentials: EmailCredentials): Promise<ConnectionTestResponse> => {
    try {
      const payload: any = { email: credentials.email };
      if (credentials.accessToken) {
        payload.accessToken = credentials.accessToken;
      } else if (credentials.password) {
        payload.password = credentials.password;
      }
      const response = await api.post<ConnectionTestResponse>('/email/test-connection', payload);
      return response.data;
    } catch (error: any) {
      console.error('Email API call error (testConnection):', error); // Log for debugging
      if (error?.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const data = error.response.data;
        let message = 'Connection test failed';
        if (data) {
          if (typeof data === 'object' && data.message) {
            message = data.message;
          } else if (typeof data === 'string') {
            // Try to extract a meaningful message from string response
            message = data.length > 200 ? data.substring(0, 200) + '...' : data;
          } else {
            message = String(data);
          }
        } else {
          message = `Server returned ${error.response.status}`;
        }
        return {
          success: false,
          message,
          error: error.response?.data?.error || undefined
        };
      } else if (error.request) {
        // The request was made but no response was received
        return {
          success: false,
          message: 'No response from server - please check your connection or try again later',
          error: 'NO_RESPONSE'
        };
      } else {
        // Something happened in setting up the request that triggered an Error
        return {
          success: false,
          message: error.message || 'An unexpected error occurred',
          error: error.message
        };
      }
    }
  },

  // Send email with attachments
  sendEmail: async (credentials: EmailCredentials, emailData: EmailData): Promise<EmailResponse> => {
    try {
      const formData = new FormData();

      // Add credentials
      formData.append('email', credentials.email);
      if (credentials.accessToken) {
        formData.append('accessToken', credentials.accessToken);
      }
      if (credentials.refreshToken) {
        formData.append('refreshToken', credentials.refreshToken);
      }
      if (credentials.password) {
        formData.append('password', credentials.password);
      }

      // Add email data
      formData.append('to', emailData.to);
      formData.append('subject', emailData.subject);
      formData.append('html', emailData.html);

      // Add attachments
      if (emailData.attachments) {
        emailData.attachments.forEach((file) => {
          formData.append('attachments', file);
        });
      }

      const response = await api.post<EmailResponse>('/email/send', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error: any) {
      console.error('Email API call error (sendEmail):', error); // Log for debugging
      if (error?.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const data = error.response.data;
        let message = 'Failed to send email';
        if (data) {
          if (typeof data === 'object' && data.message) {
            message = data.message;
          } else if (typeof data === 'string') {
            // Try to extract a meaningful message from string response
            message = data.length > 200 ? data.substring(0, 200) + '...' : data;
          } else {
            message = String(data);
          }
        } else {
          message = `Server returned ${error.response.status}`;
        }
        throw new Error(message);
      } else if (error.request) {
        // The request was made but no response was received
        throw new Error('No response from server - please check your connection or try again later');
      } else {
        // Something happened in setting up the request that triggered an Error
        throw new Error(error.message || 'An unexpected error occurred');
      }
    }
  },

  // Get email configuration
  getConfig: async () => {
    try {
      const response = await api.get('/email/config');
      return response.data;
    } catch (error: any) {
      console.error('Failed to get email config:', error);
      return null;
    }
  }
};